import type { CardObject, CardString, GameState, PlayCommand } from '../../lib';
import {
	compareRank,
	getCardObjectFromString,
	getCardsInHand,
	getCardString,
	getRankValue,
	getTrickWinner,
	getValidActions,
	Rank,
	Suit,
} from '../../lib';

// Card-probability model for opponents' hidden hands.
//
// The engine tracks every card's pile, so opponents' hands are technically
// visible. Per the design we deliberately treat them as uncertain: we know the
// SET of cards opponents hold (the union of their `hand{p}` piles) but not which
// opponent holds which. At an opponent decision node we enumerate plausible
// plays from that pool, weight each by a point-avoidance policy, and (for
// followers) by the probability the opponent actually holds the lead suit. The
// result is a probability distribution used as an expectation (chance) node in
// the search.

export interface OpponentCandidate {
	card: CardObject;
	probability: number;
}

function cardPoints(card: CardObject): number {
	if (card.suit === Suit.Hearts) {
		return 1;
	}

	if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
		return 13;
	}

	return 0;
}

// All cards still held by opponents (any `hand{p}` pile with p !== botPos).
// Cards already played sit in `trick{p}` / `trickWinnings{p}` piles, so they are
// excluded automatically.
export function getUnknownPool(state: GameState, botPos: number): CardObject[] {
	return state.deck.filter((c) => c.pile.startsWith('hand') && c.pile !== `hand${botPos}`);
}

// P(at least one card of `suit` remains in the pool belonging to a hand of size
// `handSize`), estimated via the complement of the hypergeometric
// no-suit probability. Computed as a stable product to avoid overflow.
function probHasSuit(pool: CardObject[], suit: Suit, handSize: number): number {
	const unknowns = pool.length;
	const suitCount = pool.filter((c) => c.suit === suit).length;
	const nonSuit = unknowns - suitCount;

	if (suitCount === 0) {
		return 0;
	}

	if (nonSuit < handSize) {
		return 1;
	}

	let pNo = 1;

	for (let i = 0; i < handSize; ++i) {
		pNo *= (nonSuit - i) / (unknowns - i);
	}

	return 1 - pNo;
}

// Deduce, purely from observed plays, whether `oppPos` is void in `suit` this
// round: if they have ever played off-suit when `suit` was led, they cannot
// follow suit. This sharpens the chance node (a void opponent is certain to
// play off-suit) without peeking at hidden hands.
function isVoidInSuit(state: GameState, oppPos: number, suit: Suit): boolean {
	for (const round of state.rounds) {
		for (const trick of round.tricks) {
			const leadEntry = trick.cardsPlayed[trick.leadPlayer];

			if (typeof leadEntry !== 'string') {
				continue;
			}

			const lead = getCardObjectFromString(leadEntry);

			if (lead.suit !== suit) {
				continue;
			}

			const oppPlay = trick.cardsPlayed[oppPos];

			if (typeof oppPlay === 'string') {
				const c = getCardObjectFromString(oppPlay);

				if (c.suit !== suit) {
					return true;
				}
			}
		}
	}

	return false;
}

// Point-avoidance policy weight for an opponent playing `card`.
// - Capturing points (winning the trick with a heart / Q♠, or leading a point
//   card that is likely to win) is strongly disfavored (weight 0.1).
// - Dumping a point card that does NOT win is favored (weight 1.0).
// - Non-point cards are neutral (0.5).
function policyWeight(card: CardObject, winningCard: CardObject | null): number {
	const points = cardPoints(card);
	let wouldWin = false;

	if (winningCard) {
		wouldWin = card.suit === winningCard.suit && compareRank(card.rank, winningCard.rank) > 0;
	}

	if (points > 0 && (wouldWin || winningCard === null)) {
		return 0.1;
	}

	if (points > 0) {
		return 1.0;
	}

	// Non-point lead: real opponents lead low to avoid winning the trick, so
	// weight low leads far higher than high ones (Ace -> 0.2, deuce -> 1.0).
	if (winningCard === null) {
		const rv = getRankValue(card.rank);

		return 1 - ((rv - 2) / 12) * 0.8;
	}

	return 0.5;
}

export function getOpponentCandidates(
	state: GameState,
	oppPos: number,
	botPos: number,
	topK = 5,
): OpponentCandidate[] {
	const pool = getUnknownPool(state, botPos);

	if (pool.length === 0) {
		return [];
	}

	const round = state.rounds.at(-1);
	const trick = round?.tricks.at(-1);
	const played = trick
		? trick.cardsPlayed.filter((c): c is CardString => typeof c === 'string')
		: [];

	const isLeading = played.length === 0;
	const handSize = getCardsInHand(state.deck, oppPos).length;

	let weighted: Array<{ card: CardObject; weight: number }> = [];

	if (isLeading) {
		for (const card of pool) {
			weighted.push({
				card,
				weight: policyWeight(card, null), 
			});
		}
	} else {
		const leadCard = getCardObjectFromString(trick!.cardsPlayed[trick!.leadPlayer]);
		const winningPlayer = getTrickWinner(trick!);
		const winningCard = winningPlayer !== null && trick!.cardsPlayed[winningPlayer]
			? getCardObjectFromString(trick!.cardsPlayed[winningPlayer])
			: null;

		const voidInLead = isVoidInSuit(state, oppPos, leadCard.suit);
		const pHas = voidInLead ? 0 : probHasSuit(pool, leadCard.suit, handSize);
		const onSuit = pool.filter((c) => c.suit === leadCard.suit);
		const offSuit = pool.filter((c) => c.suit !== leadCard.suit);

		for (const card of onSuit) {
			weighted.push({
				card,
				weight: policyWeight(card, winningCard) * pHas, 
			});
		}

		for (const card of offSuit) {
			weighted.push({
				card,
				weight: policyWeight(card, winningCard) * (1 - pHas), 
			});
		}
	}

	// Restrict to the opponent's actual legal moves so simulated plays are
	// valid. This also fixes a latent bug where arbitrary pool cards (not in the
	// opponent's hand) were "played", corrupting the model and stalling
	// quiescence once there was no depth limit.
	const legalKeys = new Set(
		(getValidActions(state, oppPos) as PlayCommand[]).map((m) => m.data.card),
	);
	weighted = weighted.filter((w) => legalKeys.has(getCardString(w.card)));

	if (weighted.length === 0) {
		const legalMoves = getValidActions(state, oppPos) as PlayCommand[];

		if (legalMoves.length === 0) {
			return [];
		}

		const uniform = 1 / legalMoves.length;

		return legalMoves.map((m) => ({
			card: getCardObjectFromString(m.data.card),
			probability: uniform,
		}));
	}

	// Normalize raw weights to a probability distribution.
	const total = weighted.reduce((acc, w) => acc + w.weight, 0);

	if (total <= 0) {
		// Fallback: uniform over the pool when policy weights collapsed.
		const uniform = 1 / weighted.length;

		return weighted.map((w) => ({
			card: w.card,
			probability: uniform, 
		}));
	}

	weighted = weighted
		.map((w) => ({
			card: w.card,
			weight: w.weight / total, 
		}))
		.sort((a, b) => b.weight - a.weight)
		.slice(0, Math.min(topK, weighted.length));

	const keptTotal = weighted.reduce((acc, w) => acc + w.weight, 0);

	return weighted.map((w) => ({
		card: w.card,
		probability: w.weight / keptTotal, 
	}));
}
